import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/error-boundary';
import { useAuthStore } from '@/stores/auth';
import './index.css';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Create a new QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
  },
});

// Resolve the Supabase session BEFORE the first render. The store persists `isAuthenticated`, so
// without this the router's guard would run against a restored-but-unverified flag: a user whose
// token expired would flash the shell before being bounced, and a signed-in user reloading on a
// deep link would be bounced to /login for a frame. `initialize()` sets isLoading false either way.
void useAuthStore.getState().initialize();

const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* Both devtools now live in `__root`, which is the only place that can see the ROUTE — and
              the route is what decides: `/preview/{token}` is a phone surface, not a dashboard page. */}
          <RouterProvider router={router} />
          <Toaster />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
